import React, { useRef, useEffect } from "react";
import styled from "styled-components";
import Chat from "./Chat"; // Change to default import
import { useChat } from "./ChatContext";
import { useLocation } from "react-router-dom";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ForumIcon from '@mui/icons-material/Forum';

const CHAT_WIDTH = 400;
const STATUS_BAR_HEIGHT = 32;

const Panel = styled.div`
  position: ${props => props.isInSocial ? 'relative' : 'fixed'};
  top: ${props => props.isInSocial ? '0' : '0'};
  right: ${props => {
    if (props.isInSocial) return '0';
    return props.isChatOpen || props.isChatLocked ? "0" : `-${CHAT_WIDTH}px`;
  }};
  width: ${props => props.isInSocial ? '100%' : `${CHAT_WIDTH}px`};
  height: ${props => props.isInSocial ? '100%' : `calc(100vh - ${STATUS_BAR_HEIGHT}px)`};
  background: ${props => props.isInSocial ? 'transparent' : '#2d2f33'};
  box-shadow: ${props => props.isInSocial ? 'none' : '-5px 0 10px rgba(0, 0, 0, 0.3)'};
  display: flex;
  flex-direction: column;
  transition: ${props => props.isInSocial ? 'none' : 'right 0.3s ease-in-out'};
  z-index: ${props => props.isInSocial ? '1' : '999'};
  pointer-events: auto;
  flex: 1;
`;

const ChatPanel = () => {
  const { 
    isChatOpen, 
    isChatLocked, 
    setIsChatOpen, 
    activeChat,
    clearActiveChat 
  } = useChat();
  const panelRef = useRef(null);
  const location = useLocation();
  const isInSocial = location.pathname === "/social";
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isInSocial && panelRef.current && !panelRef.current.contains(event.target) && !isChatLocked) {
        setIsChatOpen(false);
      }
    };

    if (isChatOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isChatOpen, setIsChatOpen, isChatLocked, isInSocial]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      // Behold activeChat i localStorage hvis chatten er låst
      if (!isChatLocked) {
        localStorage.removeItem('activeChat');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isChatLocked]);
  
  // Only render the panel content if necessary
  if (!isInSocial && !isChatOpen && !isChatLocked) {
    return null;
  }
  
  return (
    <Panel 
      ref={panelRef}
      isChatOpen={isChatOpen} 
      isChatLocked={isChatLocked}
      isInSocial={isInSocial}
    >
      {activeChat ? (
        <Chat isInSocial={isInSocial} />
      ) : (
        // Otherwise show a placeholder
        <Box 
          sx={{ 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flexDirection: 'column',
            color: 'text.secondary',
            p: 3
          }}
        >
          <ForumIcon sx={{ fontSize: 60, opacity: 0.3, mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#e0e0e0' }}>
            Select a conversation to start chatting
          </Typography>
          <Typography variant="body2" align="center" sx={{ maxWidth: 400, mt: 1, color: '#9e9e9e' }}>
            Choose a conversation from the list to view messages and chat with your colleagues.
          </Typography>
        </Box>
      )}
    </Panel>
  );
};

export default ChatPanel;
